/* THEODORE fit picker — behaviour for <theodore-fit-picker> (theme block
   theodore-fit-picker.liquid). A custom element, not an inline script, so
   the picker works wherever its markup lands: the PDP on parse, and the
   quick-add modal, whose DOMParser-fetched nodes never execute inline
   scripts. Loaded globally (defer) from theodore-head.liquid.

   THEODORE (bug #456, 15 Oct 2026): a garment with a "Made to measure" (MTM)
   Size value must be ordered as that variant under every fit except Standard
   size, and as the size picked under Standard size. reconcile() keeps the
   nested Size picker in that state: it checks the target radio and dispatches
   a bubbling change, Horizon's own pattern (quick-add.js #syncVariantSelection),
   so Horizon fetches the variant, morphs the picker and sets the form's id
   input itself. Nothing here writes the id input. A product without the MTM
   value (data-mtm-variant-id absent, no such value) is left exactly as before.
   Address rule, product page only: while Standard size is the chosen fit the
   URL carries fit=standard beside Horizon's ?variant=. A page that opens with
   no fit chosen on a real size (a ?variant= link) is switched to MTM, unless
   the URL carries fit=standard, which reopens Standard size on that size.
   Quick-add dialog, every fit-gated product (MTM or not): the dialog's product
   form is replaced by a fresh copy before the first reconcile, because a form
   the dialog reused from the previous product can be deaf to variant changes
   (see reviveDialogForm below). */
(() => {
  if (customElements.get('theodore-fit-picker')) return;

  let instanceCounter = 0;

  const MTM_VALUE = 'made to measure';
  const norm = (s) => String(s || '').trim().toLowerCase();
  // Passed to update() by the one deferred call made at connect.
  const AT_CONNECT = {};

  class TheodoreFitPicker extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound) return;
      this.dataset.bound = '1';

      const root = this;
      const q = (s) => root.querySelector(s);
      const propFit = q('.td-fit__prop');
      const propMachine = q('.td-fit__prop-machine');
      if (!propFit || !propMachine) return;
      const loggedIn = root.dataset.loggedIn === 'true';
      let profiles = [];

      // Radios must group per instance — the PDP and an open quick-add
      // modal can hold two pickers stamped from the same block id.
      const uid = 'td-fit-i' + ++instanceCounter;
      root.querySelectorAll('.td-fit__radio').forEach((r) => { r.name = uid; });

      const fmtDate = (iso) => {
        try {
          return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
        } catch { return ''; }
      };

      // THEODORE: (bug #456) a radio or <option> is Made to measure by its
      // variant id (emitted by the block) or by its value.
      const mtmId = root.dataset.mtmVariantId || '';
      const isMtm = (el) =>
        Boolean(el) && ((mtmId !== '' && el.dataset.variantId === mtmId) || norm(el.value) === MTM_VALUE);

      // The product's own Size option (Horizon's variant-picker) is a child
      // block of the standard row, so it is a descendant of this element and
      // its change events reach the listener below — which is what keeps the
      // docket in step with the size. Read it live; never cache it.
      // THEODORE: (bug #456) Made to measure is never a standard size, so it
      // reads as no size: "standard:Made to measure" can never be written.
      const readStandardSize = () => {
        const picker = root.querySelector('variant-picker');
        if (picker) {
          const checked = picker.querySelector('.variant-option input[type="radio"]:checked');
          if (checked) return isMtm(checked) ? '' : (checked.value || '').trim();
          const dropdown = picker.querySelector('.variant-option__select');
          if (dropdown) return isMtm(dropdown.selectedOptions[0]) ? '' : (dropdown.value || '').trim();
          // An option with a single value renders as text, with no input.
          const single = picker.querySelector('.variant-option__swatch-value');
          if (single) return norm(single.textContent) === MTM_VALUE ? '' : single.textContent.trim();
        }
        const marker = (q('.td-fit__variant-size')?.dataset.size || '').trim();
        if (marker && norm(marker) !== MTM_VALUE) return marker;
        const fallback = q('.td-fit__size-select');
        return fallback ? (fallback.value || '').trim() : '';
      };

      // THEODORE: (bug #456) the Size control that holds the MTM value: its
      // radios (button style) or its <select> (dropdown style, a guard: the
      // template uses buttons). null when the product has no MTM value, which
      // turns everything below off.
      const sizeControl = () => {
        const picker = root.querySelector('variant-picker');
        if (!picker) return null;
        const radios = [...picker.querySelectorAll('.variant-option input[type="radio"]')];
        const mtmRadio = radios.find(isMtm);
        if (mtmRadio) {
          const group = radios.filter((r) => r.name === mtmRadio.name);
          return {
            picker,
            select: null,
            mtm: mtmRadio,
            real: group.filter((r) => !isMtm(r)),
            current: group.find((r) => r.checked) || null,
          };
        }
        for (const select of picker.querySelectorAll('select.variant-option__select')) {
          const options = [...select.options];
          const mtmOption = options.find(isMtm);
          if (mtmOption) {
            return {
              picker,
              select,
              mtm: mtmOption,
              real: options.filter((o) => !isMtm(o)),
              current: select.selectedOptions[0] || null,
            };
          }
        }
        return null;
      };

      // The product page itself, not the quick-add dialog or a card: the only
      // place Horizon rewrites ?variant=, and so the only place the address
      // may carry the fit.
      // A detached picker (quick-add.js removes the previous product's before
      // each morph) has no dialog ancestor left to say where it was, so it is
      // never on the product page.
      const onProductPage = (ctl) =>
        root.isConnected &&
        ctl.picker.dataset.templateProductMatch === 'true' &&
        !root.closest('quick-add-dialog, dialog, product-card');

      // fit=standard while Standard size is the chosen fit, gone otherwise.
      // Written BEFORE the change is dispatched: Horizon builds its ?variant=
      // rewrite from the address as it stands then, so the marker survives it.
      const syncAddress = (ctl, standard) => {
        if (!onProductPage(ctl)) return;
        const url = new URL(window.location.href);
        if ((url.searchParams.get('fit') === 'standard') === standard) return;
        if (standard) url.searchParams.set('fit', 'standard');
        else url.searchParams.delete('fit');
        history.replaceState(history.state, '', url.toString());
      };

      // The form the line properties post with (the props' form= attribute),
      // looked up inside this picker's own section or dialog: the PDP and an
      // open quick-add dialog can carry the same form id. getAttribute, not
      // form.id: the form's own <input name="id"> shadows that property.
      const formIdInput = () => {
        const formId = propFit.getAttribute('form');
        if (!formId) return null;
        const scope = root.closest('dialog, .shopify-section') || document;
        const form = [...scope.querySelectorAll('form')].find((f) => f.getAttribute('id') === formId);
        return form ? form.querySelector('input[name="id"]') : null;
      };

      // THEODORE: (bug #456 review) the quick-add dialog's product form, made
      // fresh. Horizon's product-form-component creates its AbortController
      // once, as a field: disconnect aborts it, and a reconnect subscribes again
      // with the dead signal. quick-add.js morphs each product into the previous
      // one's dialog markup, and when the block sequences differ (a monogram
      // block, the spec selects) morph keeps the old buy-buttons and moves it,
      // which detaches and re-attaches the form. That form never hears another
      // variant change: its id input stays on the size the dialog opened on and
      // Add posts that (seen: Standard size 34 posted as 28; MTM chosen, 44
      // posted). A copy is a new element with live listeners, and cloning keeps
      // the inputs' current values. Once per dialog open, before anything here
      // changes the size. The root cause sits in product-form.js; this keeps a
      // fit-gated garment right whatever that file does.
      const dialog = root.closest('dialog');
      const reviveDialogForm = () => {
        const idInput = formIdInput();
        const form = idInput && idInput.closest('product-form-component');
        if (!dialog || !form || !dialog.contains(form)) return;
        form.replaceWith(form.cloneNode(true));
      };

      // Re-fires the checked size when the form's id input disagrees with it:
      // a variant change the form missed (quick-add's own sync or its cached
      // re-morph, before the revive above) is fetched again and heard.
      const followCheckedSize = () => {
        const checked = root.querySelector('variant-picker .variant-option input[type="radio"]:checked');
        const idInput = formIdInput();
        if (!checked || !checked.dataset.variantId || !idInput || !idInput.value) return;
        if (idInput.value !== checked.dataset.variantId) {
          checked.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };

      // The product page's sticky bar shows the variant title Horizon rendered
      // (Made to measure) until the fetch for the new size lands. On a one-option
      // product that title is the size, so write it now; Horizon's morph then
      // replaces it with the server's own.
      const stickyTitle = (ctl, value) => {
        const bar = root.closest('.shopify-section')?.querySelector('sticky-add-to-cart');
        if (!bar || bar.dataset.productId !== ctl.picker.dataset.productId) return;
        const title = bar.querySelector('.sticky-add-to-cart__info[data-single-option="true"] .sticky-add-to-cart__variant');
        if (title) title.textContent = value;
      };

      let ready = false; // set once the nested picker can hear a change (see settle)
      let reconciling = false;
      let lastReal = ''; // the last real size this picker had
      let loadedReal = ''; // the real size the page opened on (a ?variant= link)
      let restoredStandard = false; // Standard size reopened from fit=standard

      // THEODORE: (bug #456) idempotent: does nothing once the Size picker
      // matches the fit, so the change it dispatches (which bubbles back here
      // through update()) cannot loop. Runs at the START of update(), before
      // the props are computed from the size.
      const reconcile = (atConnect) => {
        // Not once detached: a late update() (the rAF after a size change, held
        // while the tab is not drawing) would otherwise write fit=standard, and
        // through Horizon ?variant=, onto the page the dialog was opened from.
        if (!ready || reconciling || !root.isConnected) return;
        const ctl = sizeControl();
        if (!ctl) return;
        const standard = root.querySelector('.td-fit__radio:checked')?.value === 'standard';
        syncAddress(ctl, standard);
        const cur = ctl.current;
        if (cur && !isMtm(cur)) lastReal = cur.value;

        const findReal = (value) => (value ? ctl.real.find((o) => o.value === value) : undefined);
        let target = null;
        if (standard) {
          // Never blocks Add: the last real size, else the one the page opened
          // on, else the first (today's default, 44/28/37/S).
          if (!cur || isMtm(cur)) {
            target =
              findReal(lastReal) ||
              findReal(loadedReal) ||
              ctl.real.find((o) => o.dataset.optionAvailable !== 'false' && !o.disabled) ||
              ctl.real[0] ||
              null;
          }
        } else if (!cur || !isMtm(cur)) {
          target = ctl.mtm;
        }
        // Once at connect, the radio on screen may not be what the form will
        // post: quick-add re-morphs its picker from cached HTML after this
        // element connects, and a reload can restore a radio but not the id.
        if (!target && atConnect && cur && cur.dataset.variantId) {
          const idInput = formIdInput();
          if (idInput && idInput.value && idInput.value !== cur.dataset.variantId) target = cur;
        }
        if (!target) return;

        reconciling = true;
        try {
          if (!isMtm(target) && onProductPage(ctl)) stickyTitle(ctl, target.value);
          if (ctl.select) {
            ctl.select.value = target.value;
            ctl.select.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            target.checked = true;
            target.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } finally {
          reconciling = false;
        }
      };

      const setProps = (fit, machine) => {
        propFit.value = fit || '';
        propMachine.value = machine || '';
        propFit.disabled = !fit;
        propMachine.disabled = !machine;
      };

      const update = (when) => {
        reconcile(when === AT_CONNECT);
        root.querySelectorAll('.td-fit__row').forEach((row) => {
          const radio = row.querySelector('.td-fit__radio');
          const active = Boolean(radio && radio.checked);
          row.classList.toggle('is-active', active);
          const reveal = row.querySelector('.td-fit__reveal');
          if (reveal) reveal.inert = !active; // hidden from AT + tab order
          row.querySelectorAll('.td-fit__reveal select, .td-fit__reveal input, .td-fit__reveal button, .td-fit__reveal a').forEach((c) => {
            if (active) c.removeAttribute('tabindex');
            else c.setAttribute('tabindex', '-1');
          });
        });
        const selected = root.querySelector('.td-fit__radio:checked');
        // THEODORE: the standard-size detail selects only submit on the
        // standard path — disabled inputs never reach the cart.
        root.querySelectorAll('[data-td-specs] select').forEach((sel) => {
          sel.disabled = !(selected && selected.value === 'standard');
        });
        if (!selected) return setProps('', '');
        if (selected.value === 'profile') {
          const sel = q('.td-fit__profile-select');
          const opt = sel && sel.selectedOptions[0];
          if (opt) {
            setProps('Your measurements — ' + opt.textContent.trim(), 'profile:' + opt.value);
            const record = q('.td-fit__record');
            if (record) record.textContent = 'Taken ' + (opt.dataset.date || '') + ' · held on file';
          } else setProps('', '');
        } else if (selected.value === 'standard') {
          const size = readStandardSize();
          size ? setProps('Standard size ' + size, 'standard:' + size) : setProps('', '');
        } else if (selected.value === 'appointment') {
          const when = root.dataset.apptDate;
          setProps('Measured at my fitting appointment' + (when ? ' — ' + when : ''), 'appointment');
        } else if (selected.value === 'code') {
          const code = (q('.td-fit__code-input').value || '').trim();
          const ok = root.dataset.codeOk === code && code;
          ok ? setProps('Measurement code ' + code, 'code:' + code) : setProps('', '');
        }
      };

      root.addEventListener('change', (e) => {
        update();
        // Horizon morphs the picker in place after its fetch, which can land
        // after this handler — settle again once that has happened.
        if (e.target instanceof Element && e.target.closest('variant-picker')) {
          requestAnimationFrame(() => update());
        }
      });
      const hasMtm = Boolean(sizeControl());
      if (hasMtm) {
        // THEODORE: (bug #456) fit=standard is only ever written by this
        // picker, when the customer chose Standard size here: reopen it (a
        // reload, or a Back the browser did not keep in its page cache). Done
        // at connect, before the signed-in /apps/fit request can answer, so
        // its auto-select always finds restoredStandard set and leaves it.
        const ctl = sizeControl();
        const std = q('.td-fit__row--standard .td-fit__radio');
        if (
          std &&
          onProductPage(ctl) &&
          !root.querySelector('.td-fit__radio:checked') &&
          new URL(window.location.href).searchParams.get('fit') === 'standard'
        ) {
          std.checked = true;
          restoredStandard = true;
        }
        // On a reload or an uncached Back, Chrome restored the fit radio (it
        // matches the per-instance name given above) well after settle and
        // pageshow, with no change event: the row showed a fit whose props were
        // never written. Not saved, not restored: the address (fit=standard)
        // is the one memory of the fit.
        root.querySelectorAll('.td-fit__radio').forEach((r) => r.setAttribute('autocomplete', 'off'));
      }
      update();

      // THEODORE: (bug #456) the first reconcile waits. In the quick-add dialog
      // this element connects (and upgrades) before its nested variant-picker
      // has attached its change listener, and quick-add.js then re-morphs that
      // picker from cached HTML; a macrotask after variant-picker is defined
      // runs after both, and after the dialog's morph has put the product form
      // in place, so that is also when the dialog's form is revived. On the PDP
      // this also stops relying on script order (scripts.liquid's modules
      // happen to run before this deferred file).
      if (hasMtm || dialog) {
        const settle = () => {
          if (!root.isConnected || ready) return;
          if (dialog) reviveDialogForm();
          const ctl = sizeControl();
          if (ctl) {
            if (ctl.current && !isMtm(ctl.current)) loadedReal = ctl.current.value;
            ready = true;
            update(AT_CONNECT);
          } else if (dialog) {
            // No MTM value: reconcile stays off, the form need only post the
            // size on screen.
            followCheckedSize();
          }
        };
        customElements.whenDefined('variant-picker').then(() => setTimeout(settle, 0));
      }

      // Sign-in dialog (logged-out only).
      const signin = q('.td-fit__signin');
      const modal = q('dialog.td-fit-modal');
      if (signin && modal) {
        signin.addEventListener('click', () => { if (!modal.open) modal.showModal(); });
        modal.addEventListener('click', (e) => {
          // Backdrop only — clicks on the card's own padding must not close.
          if (e.target !== modal) return;
          // THEODORE: inside the quick-add dialog this dialog is nested in another one,
          // which reads a click on a <dialog> outside its own box as "clicked outside"
          // and closes too. Dismissing the sign-in prompt must leave the garment open.
          e.stopPropagation();
          const r = modal.getBoundingClientRect();
          if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) {
            modal.close();
          }
        });
        // THEODORE: likewise Escape — the quick-add dialog listens for the same key.
        modal.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') e.stopPropagation();
        });
      }

      const codeBtn = q('.td-fit__code-check');
      if (codeBtn) {
        const codeInput = q('.td-fit__code-input');
        const result = q('.td-fit__code-result');
        const check = async () => {
          const code = (codeInput.value || '').trim();
          if (!code) return;
          codeBtn.disabled = true;
          result.textContent = 'Checking…';
          result.classList.remove('is-found');
          try {
            const res = await fetch('/apps/fit?code=' + encodeURIComponent(code), { headers: { Accept: 'application/json' } });
            const data = await res.json();
            if (data.code_found) {
              root.dataset.codeOk = code;
              result.textContent = 'Measurements found';
              result.classList.add('is-found');
              const radio = root.querySelector('.td-fit__row--code .td-fit__radio');
              if (radio) radio.checked = true;
            } else {
              delete root.dataset.codeOk;
              result.textContent = "That code isn't in our records — check it, or book a fitting.";
            }
          } catch {
            result.textContent = 'The check didn’t go through — try again in a moment.';
          }
          codeBtn.disabled = false;
          update();
        };
        codeBtn.addEventListener('click', check);
        codeInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') { e.preventDefault(); check(); }
        });
        // Editing the code after a successful check must visibly undo it —
        // never leave a stale tick beside a code we haven't verified.
        codeInput.addEventListener('input', () => {
          if (root.dataset.codeOk && root.dataset.codeOk !== (codeInput.value || '').trim()) {
            delete root.dataset.codeOk;
            result.textContent = 'Code changed — check it again.';
            result.classList.remove('is-found');
            update();
          }
        });
      }

      if (loggedIn) {
        const loading = q('.td-fit__loading');
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 6000);
        fetch('/apps/fit', { headers: { Accept: 'application/json' }, signal: controller.signal })
          .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
          .then((data) => {
            clearTimeout(timer);
            if (loading) loading.remove();
            profiles = Array.isArray(data.profiles) ? data.profiles : [];

            if (profiles.length) {
              const row = q('.td-fit__row--measurements');
              const sel = q('.td-fit__profile-select');
              profiles.forEach((p) => {
                const opt = document.createElement('option');
                opt.value = p.id;
                opt.dataset.date = fmtDate(p.taken_at);
                opt.textContent = p.label + ' · ' + fmtDate(p.taken_at);
                sel.appendChild(opt);
              });
              row.hidden = false;
              // THEODORE: (bug #456) a Standard size the customer chose on this
              // page, reopened from fit=standard, is not overridden.
              if (!restoredStandard) row.querySelector('.td-fit__radio').checked = true;
            }

            if (data.upcoming_appointment) {
              const when = fmtDate(data.upcoming_appointment.start_at);
              root.dataset.apptDate = when;
              const sub = q('.td-fit__appt-sub');
              if (sub) sub.textContent = 'Booked — ' + when + '.';
              const note = q('.td-fit__appt-note');
              if (note) note.hidden = true;
              if (!profiles.length && !restoredStandard) {
                q('.td-fit__row--appointment .td-fit__radio').checked = true;
              }
            }

            if (Array.isArray(data.standard_sizes) && data.standard_sizes.length) {
              const sizeSel = q('.td-fit__size-select');
              if (sizeSel) {
                const current = sizeSel.value;
                sizeSel.length = 1;
                data.standard_sizes.forEach((s) => {
                  const opt = document.createElement('option');
                  opt.value = s;
                  opt.textContent = s;
                  sizeSel.appendChild(opt);
                });
                sizeSel.value = current;
              }
            }
            // book_url from the proxy is ignored: the block setting owns the booking link.
            update();
          })
          .catch(() => {
            clearTimeout(timer);
            if (loading) loading.remove();
          });
      }
    }
  }

  customElements.define('theodore-fit-picker', TheodoreFitPicker);
})();
